import type { Alert, MetricDef, MetricFormat, Rule } from '@nono/contract';
import { ArrowRight, CircleCheckBig } from 'lucide-react';
import type { ReactNode } from 'react';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { useAlerts, useRules, useTargets } from '@/lib/queries';
import { cn } from '@/lib/utils';

const MESSAGES = {
  nb: {
    title: 'Hva bør optimaliseres',
    sub: 'Åpne signaler fra regelmotoren. Nyeste først.',
    empty: 'Ingen åpne varsler. Alt er innenfor målet.',
    observed: 'observert',
    target: 'mål',
    windowChip: (n: number) => `siste ${n} d`,
    globalNote: 'Varsler er globale — segmentvalget over påvirker ikke denne visningen.',
    sevCritical: 'KRITISK',
    sevWarn: 'ADVARSEL',
    sevInfo: 'INFO',
  },
  en: {
    title: 'What to optimize',
    sub: 'Open signals from the rules engine. Newest first.',
    empty: 'No open alerts. Everything is within target.',
    observed: 'observed',
    target: 'target',
    windowChip: (n: number) => `last ${n}d`,
    globalNote: 'Alerts are global — the segment toggle above does not change this view.',
    sevCritical: 'CRITICAL',
    sevWarn: 'WARN',
    sevInfo: 'INFO',
  },
};

type Severity = 'critical' | 'warn' | 'info';
type Messages = (typeof MESSAGES)['nb'];

/** Severity → card left-tick + badge tone. critical=bad/red, warn=warn/amber, info=brand. */
const SEVERITY_BORDER: Record<Severity, string> = {
  critical: 'border-l-bad',
  warn: 'border-l-warn',
  info: 'border-l-brand',
};
const SEVERITY_TONE: Record<Severity, NonNullable<BadgeProps['tone']>> = {
  critical: 'bad',
  warn: 'warn',
  info: 'brand',
};
function normalizeSeverity(raw: string): Severity {
  if (raw === 'critical') return 'critical';
  if (raw === 'warn') return 'warn';
  return 'info';
}

function severityLabel(m: Messages, sev: Severity): string {
  if (sev === 'critical') return m.sevCritical;
  if (sev === 'warn') return m.sevWarn;
  return m.sevInfo;
}

export function AlertsPage() {
  const m = useViewMessages(MESSAGES);

  const alertsQuery = useAlerts();
  const targetsQuery = useTargets();
  const rulesQuery = useRules();

  // Gate on all three: the alert payload carries only metricKey, so we need
  // targets.defs (units) + rules (window) before rendering, or formats degrade
  // during the cold-load race.
  if (alertsQuery.isLoading || targetsQuery.isLoading || rulesQuery.isLoading) {
    return (
      <PageFrame m={m}>
        <LoadingState />
      </PageFrame>
    );
  }
  if (alertsQuery.isError || !alertsQuery.data) {
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => alertsQuery.refetch()} />
      </PageFrame>
    );
  }

  const alerts = alertsQuery.data.alerts;
  const defsByKey = buildDefsByKey(targetsQuery.data?.defs);
  const windowByRule = buildWindowByRule(rulesQuery.data?.rules);

  if (alerts.length === 0) {
    return (
      <PageFrame m={m}>
        <Card className="flex flex-col items-center justify-center gap-2 p-6 text-center sm:p-10">
          <CircleCheckBig className="size-6 text-good" aria-hidden />
          <p className="text-body text-fg-muted">{m.empty}</p>
        </Card>
      </PageFrame>
    );
  }

  // Render in the server's order (date DESC, then severity rank DESC from
  // recentOpenAlerts) — do NOT re-group, which would reorder and break parity.
  // Severity still drives each card's left-border + badge.
  return (
    <PageFrame m={m}>
      <div className="flex flex-col gap-3">
        {alerts.map((a) => (
          <AlertCard
            key={a.id}
            m={m}
            alert={a}
            severity={normalizeSeverity(a.severity)}
            def={defsByKey.get(a.metricKey) ?? null}
            windowDays={windowByRule.get(a.ruleName) ?? null}
          />
        ))}
      </div>
    </PageFrame>
  );
}

function PageFrame({ m, children }: { m: Messages; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-title text-fg">{m.title}</h1>
          <CaveatRef id="rulesWindowBlended" />
        </div>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
        <p className="mt-1 text-label text-fg-subtle">{m.globalNote}</p>
      </header>
      {children}
    </div>
  );
}

function AlertCard({
  m,
  alert,
  severity,
  def,
  windowDays,
}: {
  m: Messages;
  alert: Alert;
  severity: Severity;
  def: MetricDef | null;
  windowDays: number | null;
}) {
  // Unit-aware formatting: the alert payload only carries metricKey, so we drive
  // the unit off the metric registry. Unknown key → plain int (never a wrong
  // unit suffix). null → em dash.
  const format: MetricFormat = def?.format ?? 'int';

  return (
    <Card className={cn('border-l-4 p-4', SEVERITY_BORDER[severity])}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={SEVERITY_TONE[severity]}>{severityLabel(m, severity)}</Badge>
        <strong className="text-body font-semibold text-fg">{alert.ruleName}</strong>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-label text-fg-muted">
        <span className="tabular">{formatDate(alert.date)}</span>
        {windowDays != null ? (
          <span className="inline-flex items-center gap-x-2">
            <span aria-hidden className="text-fg-subtle">
              ·
            </span>
            <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 text-micro text-fg-subtle tabular">
              {m.windowChip(windowDays)}
            </span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-x-2">
          <span aria-hidden className="text-fg-subtle">
            ·
          </span>
          <span>
            {m.observed}{' '}
            <span className="font-medium text-bad-fg tabular">
              {formatMetric(alert.observedValue, format)}
            </span>
          </span>
        </span>
        <span className="inline-flex items-center gap-x-2">
          <span aria-hidden className="text-fg-subtle">
            ·
          </span>
          <span>
            {m.target}{' '}
            <span className="font-medium text-fg tabular">
              {formatMetric(alert.threshold, format)}
            </span>
          </span>
        </span>
      </div>

      <p className="mt-2 text-body text-fg">{alert.message}</p>

      {alert.recommendation ? (
        <p className="mt-2 flex items-start gap-1.5 text-label text-fg-muted">
          <ArrowRight className="mt-0.5 size-3.5 shrink-0 text-brand" aria-hidden />
          <span>{alert.recommendation}</span>
        </p>
      ) : null}
    </Card>
  );
}

/** 'YYYY-MM-DD' → 'DD.MM.YY' (legacy fmtDate parity). null/invalid → em dash. */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const parts = iso.split('-');
  const y = parts[0];
  const mo = parts[1];
  const d = parts[2];
  if (!y || !mo || !d) return '—';
  return `${d}.${mo}.${y.slice(2)}`;
}

function buildDefsByKey(defs: MetricDef[] | undefined): Map<string, MetricDef> {
  const map = new Map<string, MetricDef>();
  if (!defs) return map;
  for (const d of defs) map.set(d.key, d);
  return map;
}

/** Map ruleName → windowDays so the window can show as a chip, not buried in prose. */
function buildWindowByRule(rules: Rule[] | undefined): Map<string, number> {
  const map = new Map<string, number>();
  if (!rules) return map;
  for (const r of rules) map.set(r.name, r.windowDays);
  return map;
}
